// TradingViewChart.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";
import {
  PRICE_LINE_COLOR_DARK,
  PRICE_LINE_COLOR_LIGHT,
  PRICE_LINE_WIDTH,
} from "./chartSettings";
import { getAuthenticatedWebSocketUrl, getToken } from "../../utilities/authHelper";
import { getRawTickerFromCatalogValue } from "../../utilities/marketSymbols";
import {
  formatLightweightChartTime,
  formatLightweightTickLabel,
  formatUserDateTimeWithZone,
  parseUtcTimestampToMs,
} from "../CoTrader/Utilities/timezone";
import EmLoader from "../Common/EmLoader";
import { useResolvedDark } from "../../ThemeProvider/ThemeContext";

const API_URL = process.env.REACT_APP_API_URL;
const PRICE_PRECISION = 4;
const PRICE_MIN_MOVE = 0.0001;
const CANDLE_SHOW_BAR_WIDTH_PX = 5.5;
const CANDLE_HIDE_BAR_WIDTH_PX = 4.5;
const ZOOM_IN_BAR_DELTA = 0.5;
const USER_ZOOM_GESTURE_WINDOW_MS = 750;

// ---- helpers ----------------------------------------------------
function mapYahooToCandles(records) {
  if (!Array.isArray(records)) return [];
  const tsKeys = ["Datetime", "Date", "timestamp", "time", "datetime", "date"];

  const out = [];
  for (const r of records) {
    let t;
    for (const k of tsKeys) {
      if (r?.[k] != null) {
        t = r[k];
        break;
      }
    }
    if (t == null) continue;

    const timestampMs = parseUtcTimestampToMs(t);
    if (!Number.isFinite(timestampMs)) continue;
    const epochSec = Math.floor(timestampMs / 1000);

    const open = +r.Open,
      high = +r.High,
      low = +r.Low,
      close = +r.Close;
    const volume = r.Volume != null ? +r.Volume : 0;

    if (![epochSec, open, high, low, close].every(Number.isFinite)) continue;

    out.push({
      time: epochSec,
      open,
      high,
      low,
      close,
      volume: Number.isFinite(volume) ? volume : 0,
    });
  }

  out.sort((a, b) => a.time - b.time);
  const dedup = [];
  let lastT = null;
  for (const row of out) {
    if (row.time !== lastT) dedup.push(row);
    lastT = row.time;
  }
  return dedup;
}

function formatPrice(value, precision = PRICE_PRECISION) {
  return Number.isFinite(value) ? value.toFixed(precision) : "-";
}

function getVisibleBarCount(range) {
  const from = Number(range?.from);
  const to = Number(range?.to);

  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return null;
  }

  return Math.max(1, to - from);
}

// ✅ bucket time for realtime tick -> candle (based on interval)
function bucketEpochSec(epochSec, interval) {
  const iv = String(interval || "1m");
  const mins = (n) => Math.floor(epochSec / (n * 60)) * (n * 60);

  if (iv === "1m") return mins(1);
  if (iv === "5m") return mins(5);
  if (iv === "15m") return mins(15);
  if (iv === "1h") return Math.floor(epochSec / 3600) * 3600;
  if (iv === "1d") return Math.floor(epochSec / 86400) * 86400;
  if (iv === "1wk") return Math.floor(epochSec / (86400 * 7)) * (86400 * 7);
  if (iv === "1mo") {
    const d = new Date(epochSec * 1000);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 1000;
  }
  return mins(1);
}

function parseStreamCurrentPrice(rawPrice) {
  if (rawPrice == null) return null;

  if (typeof rawPrice === "object") {
    return rawPrice;
  }

  if (typeof rawPrice === "number") {
    return Number.isFinite(rawPrice)
      ? {
          current_price: rawPrice,
          spot_midpoint: rawPrice,
          spot_ask: rawPrice,
          spot_bid: rawPrice,
          timestamp: new Date().toISOString(),
        }
      : null;
  }

  if (typeof rawPrice !== "string") return null;

  try {
    const normalizedValue = rawPrice
      .replace(/'/g, '"')
      .replace(/Decimal\("([^"]+)"\)/g, "$1");
    const parsedValue = JSON.parse(normalizedValue);

    if (typeof parsedValue === "number") {
      return Number.isFinite(parsedValue)
        ? {
            current_price: parsedValue,
            spot_midpoint: parsedValue,
            spot_ask: parsedValue,
            spot_bid: parsedValue,
            timestamp: new Date().toISOString(),
          }
        : null;
    }

    if (typeof parsedValue === "object" && parsedValue !== null) {
      return parsedValue;
    }
  } catch {
    const fallbackValue = Number(rawPrice);
    return Number.isFinite(fallbackValue)
      ? {
          current_price: fallbackValue,
          spot_midpoint: fallbackValue,
          spot_ask: fallbackValue,
          spot_bid: fallbackValue,
          timestamp: new Date().toISOString(),
        }
      : null;
  }

  return null;
}
// ----------------------------------------------------------------

export default function TradingViewChart({
  symbol,
  streamSymbol,
  titleOverride,
  defaultPeriod = "5d",
  defaultInterval = "1m",
  periodOverride = null,
  intervalOverride = null,
  onPeriodChange = null,
  onIntervalChange = null,
  dark,
  hideHeader = false,
  layoutVersion = 0,
  liveQuote,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef({
    candle: null,
    closeLine: null,
  });
  const tooltipRef = useRef(null);
  const resizeObsRef = useRef(null);
  const lastAppliedExternalTickRef = useRef(null);
  const chartDisplayModeRef = useRef(null);
  const baselineVisibleBarsRef = useRef(null);
  const hasUserZoomedInRef = useRef(false);
  const lastZoomGestureAtRef = useRef(0);

  // ✅ refs for realtime
  const candlesRef = useRef([]);
  const liveWsRef = useRef(null);

  const [period, setPeriod] = useState(defaultPeriod);
  const [interval, setInterval] = useState(defaultInterval);
  const [loading, setLoading] = useState(false);
  const [lastMeta, setLastMeta] = useState(null);
  const [err, setErr] = useState(null);
  const effectivePeriod = periodOverride ?? period;
  const effectiveInterval = intervalOverride ?? interval;

  useEffect(() => {
    setPeriod(defaultPeriod);
  }, [defaultPeriod]);

  useEffect(() => {
    setInterval(defaultInterval);
  }, [defaultInterval]);

  // ✅ IMPORTANT: when `dark` prop changes, we want chart theme to update too.
  const isDark = useResolvedDark(dark);

  const ui = useMemo(() => {
    if (isDark) {
      return {
        bg: "#0b1220",
        panel: "#0f172a",
        border: "rgba(255,255,255,0.10)",
        text: "#e5e7eb",
        muted: "rgba(229,231,235,0.70)",
        grid: "rgba(255,255,255,0.06)",
        tooltipBg: "rgba(15,23,42,0.92)",
        tooltipText: "#e5e7eb",
        upCandle: "#26a69a",
        downCandle: "#ef5350",
      };
    }
    return {
      bg: "#ffffff",
      panel: "#f8f9fa",
      border: "rgba(0,0,0,0.10)",
      text: "#111827",
      muted: "rgba(17,24,39,0.65)",
      grid: "rgba(0,0,0,0.06)",
      tooltipBg: "rgba(255,255,255,0.92)",
      tooltipText: "#111827",
      upCandle: "#26a69a",
      downCandle: "#ef5350",
    };
  }, [isDark]);

  const recomputeDerived = useCallback(() => {
    const candles = candlesRef.current || [];
    const { closeLine } = seriesRef.current;
    closeLine?.setData(candles.map((c) => ({ time: c.time, value: c.close })));
  }, []);

  const applyAdaptiveSeriesVisibility = useCallback((logicalRange = null) => {
    const chart = chartRef.current;
    const { candle, closeLine } = seriesRef.current;
    if (!chart || !candle || !closeLine) return;

    const visibleRange =
      logicalRange || chart.timeScale().getVisibleLogicalRange?.();
    const timeScaleWidth =
      chart.timeScale().width?.() || containerRef.current?.clientWidth || 0;

    if (!visibleRange || timeScaleWidth <= 0) {
      return;
    }

    const visibleBars = getVisibleBarCount(visibleRange);
    if (!Number.isFinite(visibleBars)) {
      return;
    }

    const pixelsPerBar = timeScaleWidth / visibleBars;
    const currentMode = chartDisplayModeRef.current;
    let nextMode = "line";

    if (hasUserZoomedInRef.current) {
      const shouldShowCandles =
        currentMode === "candles"
          ? pixelsPerBar >= CANDLE_HIDE_BAR_WIDTH_PX
          : pixelsPerBar >= CANDLE_SHOW_BAR_WIDTH_PX;
      nextMode = shouldShowCandles ? "candles" : "line";
    }

    if (currentMode === nextMode) {
      return;
    }

    const shouldShowCandles = nextMode === "candles";
    chartDisplayModeRef.current = nextMode;
    candle.applyOptions({
      visible: shouldShowCandles,
      priceLineVisible: shouldShowCandles,
      lastValueVisible: shouldShowCandles,
    });
    closeLine.applyOptions({
      visible: !shouldShowCandles,
      priceLineVisible: !shouldShowCandles,
      lastValueVisible: !shouldShowCandles,
    });
  }, []);

  const applyLiveTickToCandles = useCallback(
    (price, tickEpochSec) => {
      const candles = candlesRef.current || [];
      if (!candles.length || !Number.isFinite(price) || !Number.isFinite(tickEpochSec)) {
        return;
      }

      const tBucket = bucketEpochSec(tickEpochSec, effectiveInterval);
      const last = candles[candles.length - 1];

      if (last && last.time === tBucket) {
        const next = {
          ...last,
          high: Math.max(last.high, price),
          low: Math.min(last.low, price),
          close: price,
        };
        candles[candles.length - 1] = next;
        candlesRef.current = candles;

        seriesRef.current.candle?.update(next);
        seriesRef.current.closeLine?.update({ time: next.time, value: next.close });
        recomputeDerived();
        return;
      }

      const next = {
        time: tBucket,
        open: price,
        high: price,
        low: price,
        close: price,
      };
      candles.push(next);
      candlesRef.current = candles;

      seriesRef.current.candle?.setData(candles);
      recomputeDerived();
    },
    [effectiveInterval, recomputeDerived]
  );

  // ---- fetcher ---------------------------------------------------
  async function fetchData(sym, p, i) {
    setLoading(true);
    setErr(null);
    try {
      const quoteSymbol = getRawTickerFromCatalogValue(sym);
      const token = getToken();
      const url = `${process.env.REACT_APP_API_URL}/globallink/prices/get_prices_yf?symbol=${encodeURIComponent(
        quoteSymbol
      )}&period=${encodeURIComponent(p)}&interval=${encodeURIComponent(i)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(
          json?.errorObject?.message ||
            json?.detail ||
            json?.message ||
            json?.error ||
            "API error",
        );
      }
      if (json?.results?.error) throw new Error(json.results.error);

      const records = json.results.data || [];
      const candles = mapYahooToCandles(records);

      setLastMeta({
        currency: json.results?.info?.currency,
        longName: json.results?.info?.longName,
        dataPoints: json.results?.data_points,
      });

      return candles;
    } catch (e) {
      setErr(e.message || String(e));
      return [];
    } finally {
      setLoading(false);
    }
  }

  // ---- chart init (create once per (symbol) and recreate if theme changes) ---
  useEffect(() => {
    if (!containerRef.current) return;
    const containerElement = containerRef.current;

    // cleanup any existing chart first
    try {
      chartRef.current?.remove();
    } catch (_) {}
    chartRef.current = null;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: ui.bg },
        textColor: ui.text,
        attributionLogo: false,
      },
      localization: {
        priceFormatter: (price) => formatPrice(price),
        timeFormatter: (time) =>
          formatLightweightChartTime(time, effectiveInterval),
      },
      grid: {
        vertLines: { color: ui.grid },
        horzLines: { color: ui.grid },
      },
      timeScale: {
        borderColor: ui.border,
        timeVisible: true,
        secondsVisible: ["1m", "5m", "15m"].includes(effectiveInterval),
        tickMarkFormatter: (time, tickMarkType) =>
          formatLightweightTickLabel(time, tickMarkType, effectiveInterval),
      },
      rightPriceScale: {
        borderColor: ui.border,
      },
      crosshair: { mode: CrosshairMode.Normal },
    });

    const candle = chart.addCandlestickSeries({
      visible: false,
      upColor: ui.upCandle,
      downColor: ui.downCandle,
      borderUpColor: ui.upCandle,
      borderDownColor: ui.downCandle,
      wickUpColor: ui.upCandle,
      wickDownColor: ui.downCandle,
      priceLineVisible: true,
      lastValueVisible: true,
      priceFormat: {
        type: "price",
        precision: PRICE_PRECISION,
        minMove: PRICE_MIN_MOVE,
      },
    });

    const closeLine = chart.addLineSeries({
      color: isDark ? PRICE_LINE_COLOR_DARK : PRICE_LINE_COLOR_LIGHT,
      lineWidth: PRICE_LINE_WIDTH,
      priceLineVisible: true,
      lastValueVisible: true,
      priceFormat: {
        type: "price",
        precision: PRICE_PRECISION,
        minMove: PRICE_MIN_MOVE,
      },
    });

    chartRef.current = chart;
    seriesRef.current = { candle, closeLine };

    // Tooltip
    const tooltip = document.createElement("div");
    tooltip.style.position = "absolute";
    tooltip.style.left = "8px";
    tooltip.style.top = "8px";
    tooltip.style.zIndex = "10";
    tooltip.style.padding = "8px 10px";
    tooltip.style.borderRadius = "8px";
    tooltip.style.fontSize = "12px";
    tooltip.style.pointerEvents = "none";
    tooltip.style.border = `1px solid ${ui.border}`;
    tooltip.style.boxShadow = isDark
      ? "0 2px 10px rgba(0,0,0,0.55)"
      : "0 2px 10px rgba(0,0,0,0.12)";
    tooltip.style.background = ui.tooltipBg;
    tooltip.style.color = ui.tooltipText;

    tooltipRef.current = tooltip;
    containerElement.style.position = "relative";
    containerElement.appendChild(tooltip);

    chart.subscribeCrosshairMove((param) => {
      if (!param || !param.time || !param.seriesPrices) return;

      const candleSeries = seriesRef.current.candle;
      const seriesValues = param.seriesData || param.seriesPrices;
      const seriesPrice = seriesValues?.get
        ? seriesValues.get(candleSeries)
        : seriesValues?.[candleSeries];
      const hoveredCandle =
        seriesPrice && typeof seriesPrice === "object" && "open" in seriesPrice
          ? seriesPrice
          : candlesRef.current.find((item) => item.time === +param.time);
      if (!hoveredCandle) return;
      const { open, high, low, close } = hoveredCandle;

      tooltip.innerHTML = `
        <div><strong>${symbol}</strong></div>
        <div style="opacity:0.85">${formatUserDateTimeWithZone(+param.time * 1000)}</div>
        <div>O: ${formatPrice(open)}  H: ${formatPrice(high)}  L: ${formatPrice(
        low
      )}  C: ${formatPrice(close)}</div>
      `;
    });

    const syncChartToContainer = (width, height) => {
      chart.resize(width, height, true);
    };

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        // Hidden kept-alive tab (display:none): keep the last size and
        // visible range instead of collapsing the chart to 0x0.
        if (width === 0 || height === 0) continue;
        syncChartToContainer(width, height);
        requestAnimationFrame(() => applyAdaptiveSeriesVisibility());
      }
    });
    resizeObsRef.current = ro;
    ro.observe(containerElement);

    const handleWheel = () => {
      lastZoomGestureAtRef.current = Date.now();
    };
    const handleTouchMove = (event) => {
      if ((event.touches?.length || 0) > 1) {
        lastZoomGestureAtRef.current = Date.now();
      }
    };

    containerElement.addEventListener("wheel", handleWheel, {
      passive: true,
    });
    containerElement.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    });

    const handleVisibleLogicalRangeChange = (range) => {
      const baselineVisibleBars = baselineVisibleBarsRef.current;
      const visibleBars = getVisibleBarCount(range);
      const zoomGestureIsRecent =
        Date.now() - lastZoomGestureAtRef.current <= USER_ZOOM_GESTURE_WINDOW_MS;

      if (
        !hasUserZoomedInRef.current &&
        zoomGestureIsRecent &&
        Number.isFinite(baselineVisibleBars) &&
        Number.isFinite(visibleBars) &&
        visibleBars < baselineVisibleBars - ZOOM_IN_BAR_DELTA
      ) {
        hasUserZoomedInRef.current = true;
      }

      applyAdaptiveSeriesVisibility(range);
    };
    chart
      .timeScale()
      .subscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);

    return () => {
      try {
        chart
          .timeScale()
          .unsubscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);
      } catch (_) {}
      containerElement.removeEventListener("wheel", handleWheel);
      containerElement.removeEventListener("touchmove", handleTouchMove);
      if (tooltipRef.current && tooltipRef.current.parentNode) {
        tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      }
      try {
        ro.disconnect();
      } catch (_) {}
      try {
        chart.remove();
      } catch (_) {}
      chartRef.current = null;
      chartDisplayModeRef.current = null;
      baselineVisibleBarsRef.current = null;
      hasUserZoomedInRef.current = false;
      lastZoomGestureAtRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyAdaptiveSeriesVisibility, isDark, symbol, ui.bg, ui.text, ui.grid, ui.border, effectiveInterval]);

  // ---- load historical data once per symbol/period/interval ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      const candles = await fetchData(symbol, effectivePeriod, effectiveInterval);
      if (!alive || !chartRef.current) return;

      const { candle, closeLine } = seriesRef.current;

      candle.setData(candles);
      candlesRef.current = candles;

      closeLine.setData(candles.map((c) => ({ time: c.time, value: c.close })));
      chartRef.current.timeScale().fitContent();
      const baselineRange = chartRef.current.timeScale().getVisibleLogicalRange?.();

      baselineVisibleBarsRef.current = getVisibleBarCount(baselineRange);
      hasUserZoomedInRef.current = false;
      lastZoomGestureAtRef.current = 0;
      chartDisplayModeRef.current = null;
      applyAdaptiveSeriesVisibility(baselineRange);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyAdaptiveSeriesVisibility, symbol, effectivePeriod, effectiveInterval, isDark]);

  // ✅ REALTIME WS: updates candles continuously after historical load
  useEffect(() => {
    if (liveQuote !== undefined) {
      return undefined;
    }

    if (!symbol || !API_URL) return;

    try {
      liveWsRef.current?.close();
    } catch (_) {}
    liveWsRef.current = null;

    const normalizeSym = (s) =>
      getRawTickerFromCatalogValue(s)
        .trim()
        .replace(/=x$/i, "")
        .toLowerCase();

    const liveSymbol = getRawTickerFromCatalogValue(streamSymbol || symbol || "");
    const chartSym = normalizeSym(liveSymbol);
    const chatId = `PRICE:${liveSymbol},`;
    const userId = "chart_ticker_1";
    let isCancelled = false;
    let ws;

    const openSocket = async () => {
      try {
        const wsUrl = await getAuthenticatedWebSocketUrl(chatId, userId);
        if (isCancelled) return;

        ws = new WebSocket(wsUrl);
        liveWsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            const msgSym = normalizeSym(data.symbol || data.currency_pair || data.ticker);
            if (msgSym && msgSym !== chartSym) return;

            const raw = data.raw || {};
            const parsedCurrentPrice =
              parseStreamCurrentPrice(data.current_price) ||
              parseStreamCurrentPrice(raw.current_price);
            const priceCandidates = [
              parsedCurrentPrice?.spot_midpoint,
              parsedCurrentPrice?.current_price,
              parsedCurrentPrice?.spot_ask,
              parsedCurrentPrice?.spot_bid,
              data.price,
              data.last,
              data.ltp,
              data.close,
              data.value,
              raw.price,
              raw.last,
              raw.ltp,
              raw.close,
            ];
            const price = Number(priceCandidates.find((x) => x != null));
            if (!Number.isFinite(price)) return;

            const t =
              parsedCurrentPrice?.timestamp ??
              data.timestamp ??
              data.time ??
              data.ts ??
              raw.timestamp ??
              raw.time ??
              raw.ts ??
              null;

            const timestampMs = parseUtcTimestampToMs(t);
            const epochSec = Number.isFinite(timestampMs)
              ? Math.floor(timestampMs / 1000)
              : Math.floor(Date.now() / 1000);

            applyLiveTickToCandles(price, epochSec);
          } catch (err) {
            console.error("[TradingViewChart] WS parse error:", err);
          }
        };

        ws.onerror = (err) => console.error("[TradingViewChart] WS error:", err);
        ws.onclose = () => {};
      } catch (err) {
        if (!isCancelled) {
          console.error("[TradingViewChart] WS auth error:", err);
        }
      }
    };

    void openSocket();

    return () => {
      isCancelled = true;
      try {
        ws.close();
      } catch (_) {}
    };
  }, [applyLiveTickToCandles, liveQuote, symbol, streamSymbol]);

  useEffect(() => {
    if (!liveQuote || typeof liveQuote !== "object") {
      return;
    }

    const rawPrice =
      liveQuote.current_price ??
      liveQuote.spot_midpoint ??
      liveQuote.price ??
      liveQuote.last;
    const nextPrice = Number(rawPrice);
    if (!Number.isFinite(nextPrice)) {
      return;
    }

    const rawTimestamp =
      liveQuote.timestamp ??
      liveQuote.time ??
      liveQuote.datetime ??
      liveQuote.date;

    let epochSec = null;
    const timestampMs = parseUtcTimestampToMs(rawTimestamp);
    if (Number.isFinite(timestampMs)) {
      epochSec = Math.floor(timestampMs / 1000);
    }

    if (!Number.isFinite(epochSec)) {
      epochSec = Math.floor(Date.now() / 1000);
    }

    const dedupeKey = `${epochSec}:${nextPrice}`;
    if (lastAppliedExternalTickRef.current === dedupeKey) {
      return;
    }
    lastAppliedExternalTickRef.current = dedupeKey;

    applyLiveTickToCandles(nextPrice, epochSec, 0);
  }, [applyLiveTickToCandles, liveQuote]);

  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;

    if (!chart || !container) {
      return undefined;
    }

    let frameId = null;
    const syncChartToContainer = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const { clientWidth, clientHeight } = container;
        if (clientWidth > 0 && clientHeight > 0) {
          chart.resize(clientWidth, clientHeight, true);
          applyAdaptiveSeriesVisibility();
        }
      });
    };

    syncChartToContainer();

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [applyAdaptiveSeriesVisibility, layoutVersion, symbol, effectiveInterval, effectivePeriod]);

  const intervals = ["1m", "5m", "15m", "1h", "1d", "1wk", "1mo"];
  const periods = ["1d", "5d", "1mo", "3mo", "6mo", "1y", "5y", "max"];

  return (
    <div
      className="h-100 w-100 d-flex flex-column"
      style={{ background: ui.bg, minHeight: 0 }}
    >
      {!hideHeader ? (
        <div
          className="d-flex align-items-center gap-2 p-2 border-bottom"
          style={{ background: ui.panel, borderColor: ui.border, color: ui.text }}
        >
          <div className="fw-semibold">
            {titleOverride || lastMeta?.longName || symbol}
            {lastMeta?.currency ? (
              <span className="ms-2" style={{ color: ui.muted }}>
                ({lastMeta.currency})
              </span>
            ) : null}
          </div>

          <div className="ms-auto d-flex align-items-center gap-2">
            <div className="d-flex align-items-center">
              <span className="me-1" style={{ color: ui.muted }}>
                Interval
              </span>
              <select
                value={effectiveInterval}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  if (typeof onIntervalChange === "function") {
                    onIntervalChange(nextValue);
                  } else {
                    setInterval(nextValue);
                  }
                }}
                className="form-select form-select-sm"
                style={{
                  width: 110,
                  background: isDark ? "#0f172a" : "#ffffff",
                  color: ui.text,
                  borderColor: ui.border,
                }}
              >
                {intervals.map((iv) => (
                  <option key={iv} value={iv}>
                    {iv}
                  </option>
                ))}
              </select>
            </div>

            <div className="d-flex align-items-center">
              <span className="me-1" style={{ color: ui.muted }}>
                Period
              </span>
              <select
                value={effectivePeriod}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  if (typeof onPeriodChange === "function") {
                    onPeriodChange(nextValue);
                  } else {
                    setPeriod(nextValue);
                  }
                }}
                className="form-select form-select-sm"
                style={{
                  width: 110,
                  background: isDark ? "#0f172a" : "#ffffff",
                  color: ui.text,
                  borderColor: ui.border,
                }}
              >
                {periods.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ) : null}

      <div
        ref={containerRef}
        style={{ flex: 1, minHeight: 0, position: "relative" }}
      >
        {loading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
            style={{
              background: isDark ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.12)",
              backdropFilter: "blur(2px)",
              zIndex: 10,
              color: ui.text,
            }}
          >
            <EmLoader size="lg" />
            <div className="mt-2 small" style={{ color: ui.muted }}>
              Updating chart…
            </div>
          </div>
        )}

        {err && (
          <div className="position-absolute top-50 start-50 translate-middle text-danger">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
